import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneTilt, Plus, Trash } from "@phosphor-icons/react";
import DatePicker from "@/components/DatePicker";
import AttachmentUploader from "@/components/AttachmentUploader";

/* v2.91 · Client-side "Post a job" form. Writes to db.jobs. */

const F = ({ label, hint, children }) => (
  <label className="block">
    <span className="label-tag block mb-1">{label}</span>
    {children}
    {hint && <div className="mt-1 text-[10px] text-gray-500 font-mono">{hint}</div>}
  </label>
);

export default function JobNewPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "",
    description: "",
    scope: [""],
    budget_estimate_usd: 0,
    deadline: "",
    specializations_required: [""],
    location: { country: "", region: "" },
    attachments: [],
  });
  const [busy, setBusy] = useState(false);

  const patch = (p) => setForm((s) => ({ ...s, ...p }));
  const updList = (key, i, v) => {
    const list = [...form[key]]; list[i] = v; patch({ [key]: list });
  };
  const addList = (key) => patch({ [key]: [...form[key], ""] });
  const removeList = (key, i) => {
    const list = form[key].filter((_, idx) => idx !== i);
    patch({ [key]: list.length ? list : [""] });
  };

  const submit = async () => {
    const payload = {
      ...form,
      scope: form.scope.filter((s) => s.trim()),
      specializations_required: form.specializations_required.filter((s) => s.trim()),
      budget_estimate_usd: Number(form.budget_estimate_usd) || 0,
    };
    if (payload.title.trim().length < 3 || payload.description.trim().length < 10) {
      toast.error("Title (≥ 3 chars) and description (≥ 10 chars) required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/jobs", payload);
      toast.success("Job posted — engineers can now see it.");
      nav(`/jobs/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to post job");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
        <button onClick={() => nav("/jobs")} className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="job-new-back">
          <ArrowLeft size={14} /> back to jobs
        </button>
        <div className="mt-4 label-tag">// new job</div>
        <h1 className="font-display text-4xl md:text-5xl font-black mt-1">Post a project</h1>
        <p className="text-gray-400 mt-2">Vetted engineers will see this and submit RFP-style proposals.</p>

        <section className="mt-8 border border-white/10 bg-[#12151C] p-6 space-y-4">
          <F label="Title (3–200 chars)">
            <input value={form.title} onChange={(e) => patch({ title: e.target.value.slice(0, 200) })}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
              placeholder="e.g. Retrofit MCC-3 for new stamping line" data-testid="job-new-title" />
          </F>
          <F label="Description" hint="Explain what needs to be done, constraints, and desired outcomes.">
            <textarea value={form.description} onChange={(e) => patch({ description: e.target.value })}
              rows={5}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none resize-none"
              data-testid="job-new-description" />
          </F>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="label-tag">Scope items</span>
              <button onClick={() => addList("scope")} className="text-xs text-[#00E5FF] hover:underline font-mono" data-testid="job-new-scope-add">
                <Plus size={12} className="inline" /> add
              </button>
            </div>
            {form.scope.map((s, i) => (
              <div key={i} className="flex items-center gap-2 mb-1" data-testid={`job-new-scope-row-${i}`}>
                <input value={s} onChange={(e) => updList("scope", i, e.target.value)}
                  className="flex-1 bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                  placeholder="e.g. Short-circuit study" data-testid={`job-new-scope-${i}`} />
                <button onClick={() => removeList("scope", i)} className="text-[#FF3B30] hover:underline text-xs"><Trash size={14} /></button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Budget estimate (USD)">
              <input type="number" min={0} value={form.budget_estimate_usd}
                onChange={(e) => patch({ budget_estimate_usd: e.target.value })}
                className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                data-testid="job-new-budget" />
            </F>
            <F label="Deadline">
              <DatePicker value={form.deadline}
                onChange={(v) => patch({ deadline: v })}
                placeholder="pick a deadline"
                testid="job-new-deadline" />
            </F>
            <F label="Country">
              <input value={form.location.country}
                onChange={(e) => patch({ location: { ...form.location, country: e.target.value } })}
                className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                data-testid="job-new-country" />
            </F>
            <F label="State / province">
              <input value={form.location.region}
                onChange={(e) => patch({ location: { ...form.location, region: e.target.value } })}
                className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                data-testid="job-new-region" />
            </F>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="label-tag">Specializations required</span>
              <button onClick={() => addList("specializations_required")} className="text-xs text-[#00E5FF] hover:underline font-mono" data-testid="job-new-spec-add">
                <Plus size={12} className="inline" /> add
              </button>
            </div>
            {form.specializations_required.map((s, i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <input value={s} onChange={(e) => updList("specializations_required", i, e.target.value)}
                  className="flex-1 bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                  placeholder="e.g. Arc-flash studies" data-testid={`job-new-spec-${i}`} />
                <button onClick={() => removeList("specializations_required", i)} className="text-[#FF3B30] hover:underline text-xs"><Trash size={14} /></button>
              </div>
            ))}
          </div>
          <div>
            <span className="label-tag block mb-2">Attachments (optional)</span>
            <AttachmentUploader
              endpoint="/jobs/upload-attachment"
              kind="job"
              value={form.attachments}
              onChange={(next) => patch({ attachments: next })}
              testid="job-new-attach" />
          </div>
        </section>

        <div className="mt-6 flex justify-end">
          <button onClick={submit} disabled={busy}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="job-new-submit">
            <PaperPlaneTilt size={16} weight="fill" /> {busy ? "Posting…" : "Post job"}
          </button>
        </div>
      </div>
    </div>
  );
}
